import { queryClient } from "@/api/constants"
import {
	CollectionResponseDTO,
	PhotoResponseDTO,
	PSListingResponseDTO,
	PSApiOperations,
	WatermarkResponseDTO,
} from "@/api/PSApiOperations"
import { Sentry } from "@/telemetry/sentry"
import { ProjectItem } from "../project/projects-service"
import { atom } from "jotai"
import { atomWithQuery } from "jotai-tanstack-query"
import { store } from "../store"
import { galleryIdAtom } from "./pixieset-state"
import { userQueryAtom } from "@/api/users-api"
import { isAuthReadyAtom } from "@/store/auth-state"
import { fetchAllPages } from "@/api/paginate"

export class PixiesetService {
	static listCollectionsPage = async (
		page = 1,
		q?: string,
		signal?: AbortSignal,
	): Promise<PSListingResponseDTO<CollectionResponseDTO>> => {
		return await PSApiOperations.getCollections({ page, q }, signal)
	}

	static getCollection = async (collectionId: string) => {
		return await PSApiOperations.getCollection(collectionId)
	}

	static createCollection = async (name: string, project: ProjectItem) => {
		// Convert local date to Canadian format (YYYY-MM-DD) for Pixieset
		const localDateString = project.eventStartedAt.toLocaleDateString("en-CA", {
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		})

		const response = await PSApiOperations.createCollection({
			name,
			event_date: localDateString ?? null,
			collection_default: null,
			watermark: null,
			folder: null,
		})
		void queryClient
			.invalidateQueries({ queryKey: ["collections"] })
			.catch(Sentry.captureException)
		return response
	}

	static listPhotos = async (galleryId: string) => {
		const response = await PSApiOperations.getPhotos({ galleryId })
		return response
	}

	static listGalleries = async (collectionId: string) => {
		return await fetchAllPages((params) => PSApiOperations.getGalleries(collectionId, params))
	}

	static getDefaultGalleryId = async (collectionId: string) => {
		const response = await PSApiOperations.getGalleries(collectionId)
		const gallery = response.data.find((g) => g.name === "Highlights") ?? response.data[0]
		return gallery?.id
	}
}

export const fetchPhotosAtom = atomWithQuery<PhotoResponseDTO[]>((get) => {
	const galleryId = get(galleryIdAtom)
	return {
		queryKey: ["galleryPhotos", galleryId],
		enabled: get(isAuthReadyAtom) && Boolean(galleryId),
		queryFn: () => (galleryId ? PixiesetService.listPhotos(galleryId) : []),
	}
})

export const galleryPhotosAtom = atom<{ data: PhotoResponseDTO[]; isLoading: boolean }>((get) => {
	const galleryId = get(galleryIdAtom)
	if (!galleryId) {
		return { data: [], isLoading: true }
	}
	const { data, isLoading, isInitialLoading, isError } = get(fetchPhotosAtom)

	if (galleryId && data && !isError) {
		return { data, isLoading: isLoading || isInitialLoading }
	}

	return {
		data: [],
		isLoading: isLoading || isInitialLoading,
	}
})

export const watermarksAtom = atomWithQuery<WatermarkResponseDTO[]>((get) => ({
	queryKey: ["watermarks", store.get(userQueryAtom)],
	queryFn: () => PSApiOperations.getWatermarks(),
	enabled: get(isAuthReadyAtom),
}))
